import { useState } from 'react';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Autocomplete,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Add, Close, GroupAdd } from '@mui/icons-material';
import FlexBetween from 'components/FlexBetween';


const ATTENDANCE_STATUSES = ['enrolled', 'attended', 'absent', 'withdrawn'];
const ENROLLED_AS_OPTIONS = ['trainee', 'instructor', 'admin'];

const STATUS_COLOR = {
  enrolled: 'default',
  attended: 'success',
  absent: 'warning',
  withdrawn: 'error',
};

/* ── Mobile card for one enrollment ─────────────────── */
const PassedChip = ({ passed, onClick, sx = {} }) => {
  const label = passed === true ? 'P' : passed === false ? 'F' : '–';
  const color = passed === true ? 'success' : passed === false ? 'error' : 'default';
  const variant = passed === null || passed === undefined ? 'outlined' : 'filled';
  return (
    <Chip
      label={label}
      size="small"
      color={color}
      variant={variant}
      onClick={onClick}
      sx={{ height: 18, cursor: 'pointer', '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' }, ...sx }}
    />
  );
};

const EnrollmentCard = ({
  enrollment,
  index,
  subjects,
  subjectsData,
  onRemove,
  onUpdateStatus,
  onUpdateEnrolledAs,
  onUpdateScore,
  onUpdatePassed,
}) => {
  const isTrainee = (enrollment.enrolledAs || 'trainee') === 'trainee';
  const statusLocked = enrollment.status === 'absent' || enrollment.status === 'withdrawn';

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ minWidth: 18, flexShrink: 0 }}
        >
          {index}
        </Typography>
        <Box flexGrow={1} minWidth={0}>
          <Typography variant="body2" fontWeight="bold" noWrap>
            {enrollment.employeeName || enrollment.employeeCode}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enrollment.employeeCode}{enrollment.base ? ` · ${enrollment.base}` : ''}
          </Typography>
        </Box>
        {enrollment.rank && <Chip label={enrollment.rank} size="small" sx={{ flexShrink: 0 }} />}
        <Chip
          label={enrollment.status || 'enrolled'}
          size="small"
          color={STATUS_COLOR[enrollment.status] || 'default'}
          sx={{ flexShrink: 0, minWidth: 76, justifyContent: 'center' }}
        />
        <IconButton 
          size="small" 
          color="error" 
          onClick={() => onRemove(enrollment.employeeCode)}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Role + Status selects */}
      <Box 
        display="flex" 
        gap={1} 
        mb={subjects.length > 0 && isTrainee ? 1 : 0}
      >
        <TextField
          select 
          size="small" 
          fullWidth
          label="Role" 
          value={enrollment.enrolledAs || 'trainee'}
          onChange={(ev) => onUpdateEnrolledAs(enrollment.employeeCode, ev.target.value)}
        >
          {ENROLLED_AS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select 
          size="small" 
          label="Status" 
          fullWidth
          disabled={!isTrainee}
          value={enrollment.status || 'enrolled'}
          onChange={(ev) => onUpdateStatus(enrollment.employeeCode, ev.target.value)}
        >
          {ATTENDANCE_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Score + passed inputs — only for trainee */}
      {subjects.length > 0 && isTrainee && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {subjects.map((sub) => {
            const subId = (sub._id || sub).toString();
            const subjectData = subjectsData.find((s) => s._id === subId);
            const subCode = subjectData?.subjectCode || sub.subjectCode || subId.slice(-4);
            const result = (enrollment.results || []).find(
              (r) => (r.subjectId?._id || r.subjectId || '').toString() === subId
            );
            const nextPassed = result?.passed === true ? false : result?.passed === false ? null : true;
            return (
              <Box key={subId} display="flex" alignItems="center" gap={0.5}>
                {!statusLocked && (
                  <TextField
                    type="number"
                    size="small"
                    label={subCode}
                    value={result?.score ?? ''}
                    onChange={(ev) => onUpdateScore(enrollment.employeeCode, subId, ev.target.value)}
                    slotProps={{ htmlInput: { min: 0, max: 100 } }}
                    sx={{ width: 80 }}
                  />
                )}
                <PassedChip
                  passed={result?.passed ?? null}
                  onClick={() => onUpdatePassed(enrollment.employeeCode, subId, nextPassed)}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};


/* ── Main component ──────────────────────────────────── */
const SessionAttendance = ({
  sessionId,
  isNew,
  enrollments,
  subjects,
  subjectsData,
  employeeList,
  maxCapacity,
  trainingCourseId,
  onEnrollmentsChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [newEnrolleeCode, setNewEnrolleeCode] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const addEnrollee = () => {
    if (!newEnrolleeCode) return;
    const emp = newEnrolleeCode;
    if (enrollments.some((e) => e.employeeCode === emp.employeeCode)) return;
    onEnrollmentsChange([
      ...enrollments,
      { employeeCode: emp.employeeCode, employeeName: emp.employeeName || '', rank: emp.rank || '', base: emp.homeBase || '', enrolledAs: 'trainee', status: 'enrolled', results: [] },
    ]);
    setNewEnrolleeCode(null);
  };

  const removeEnrollee = (code) =>
    onEnrollmentsChange(enrollments.filter((e) => e.employeeCode !== code));

  const updateStatus = (employeeCode, status) => {
    const clearResults = status === 'absent' || status === 'withdrawn';
    onEnrollmentsChange(enrollments.map((e) =>
      e.employeeCode === employeeCode
        ? { ...e, status, ...(clearResults ? { 
          results: subjects.map((s) => ({ 
            subjectId: s._id, 
            score: null, 
            passed: false, 
            grade: '', 
            notes: '' 
          })), } : {}) }
        : e
    ));
  };

  const updateEnrolledAs = (employeeCode, enrolledAs) =>
    onEnrollmentsChange(enrollments.map((e) =>
      e.employeeCode === employeeCode ? { ...e, enrolledAs } : e
    ));

  const updateScore = (employeeCode, subjectId, scoreValue) => {
    onEnrollmentsChange(enrollments.map((e) => {
      if (e.employeeCode !== employeeCode) return e;
      const results = [...(e.results || [])];
      const idx = results.findIndex((r) => (r.subjectId?._id || r.subjectId || '').toString() === subjectId.toString());
      const score = scoreValue === '' ? null : Number(scoreValue);
      const subjectData = subjectsData.find((s) => s._id === subjectId);
      const passed = score !== null && subjectData?.passingScore != null ? score >= subjectData.passingScore : null;
      if (idx >= 0) {
        results[idx] = { ...results[idx], score, passed };
      } else {
        results.push({ subjectId, score, passed, grade: '', notes: '' });
      }
      return { ...e, results };
    }));
  };

  const updatePassed = (employeeCode, subjectId, passed) => {
    onEnrollmentsChange(enrollments.map((e) => {
      if (e.employeeCode !== employeeCode) return e;
      const results = [...(e.results || [])];
      const idx = results.findIndex((r) => (r.subjectId?._id || r.subjectId || '').toString() === subjectId.toString());
      if (idx >= 0) {
        results[idx] = { ...results[idx], passed };
      } else {
        results.push({ subjectId, score: null, passed, grade: '', notes: '' });
      }
      return { ...e, results };
    }));
  };

  const handleImportFromCourse = async () => {
    try {
      setIsImporting(true);
      if (sessionId) {
        const res = await api.post(`/trainingsessions/${sessionId}/import-attendees`);
        toast.success(`Imported ${res.data.added} attendee(s).`);
        onEnrollmentsChange(res.data.session?.enrollments || enrollments);
      } else {
        const res = await api.get(`/trainingcourses/${trainingCourseId}`);

        const courseEnrollments = res.data?.enrollments || [];
        const existing = new Set(enrollments.map((e) => e.employeeCode));
        const toAdd = courseEnrollments
          .filter((e) => !existing.has(e.employeeCode))
          .map((e) => ({
            employeeCode: e.employeeCode,
            employeeName: e.employeeName || '',
            rank: e.rank || '',
            base: e.base || '',
            enrolledAs: e.enrolledAs || 'trainee',
            status: 'enrolled',
            results: [],
          }));
        onEnrollmentsChange([...enrollments, ...toAdd]);
        toast.success(`Imported ${toAdd.length} attendee(s).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Import failed!');
    } finally {
      setIsImporting(false);
    }
  };

  /* ── DataGrid columns (desktop only) ─────────────── */
  const scoreColumns = (subjects || []).map((sub) => {
    const subId = (sub._id || sub).toString();
    const subjectData = subjectsData.find((s) => s._id === subId);
    const subCode = subjectData?.subjectCode || sub.subjectCode || subId.slice(-4);
    return {
      field: `score_${subId}`,
      headerName: subCode,
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const result = (params.row.results || []).find(
          (r) => (r.subjectId?._id || r.subjectId || '').toString() === subId
        );
        const isTrainee = (params.row.enrolledAs || 'trainee') === 'trainee';
        const statusLocked = params.row.status === 'absent' || params.row.status === 'withdrawn';
        const nextPassed = result?.passed === true ? false : result?.passed === false ? null : true;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              height: '100%',
              width: '100%',
            }}
          >
            <TextField
              type="number"
              size="small"
              variant="standard"
              disabled={!isTrainee || statusLocked}
              value={result?.score ?? ''}
              onChange={(ev) => updateScore(params.row.employeeCode, subId, ev.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 100, style: { width: 45, textAlign: 'center' } } }}
            />
            {isTrainee && (
              <PassedChip
                passed={result?.passed ?? null}
                onClick={() => updatePassed(params.row.employeeCode, subId, nextPassed)}
              />
            )}
          </Box>
        );
      },
    };
  });

  const columns = [
    { 
      field: 'no', 
      headerName: '#', 
      width: 50, 
      sortable: false, 
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 
    },
    { 
      field: 'employeeCode', 
      headerName: 'Code', 
      width: 90 
    },
    {
      field: 'employeeName',
      headerName: 'EmployeeName',
      flex: 2,
      minWidth: 130
    },
    {
      field: 'rank',
      headerName: 'Rank',
      width: 72,
      renderCell: (params) => params.value
        ? <Chip label={params.value} size="small" />
        : <Typography variant="body2" color="text.secondary">—</Typography>
    },
    {
      field: 'base',
      headerName: 'Base',
      width: 65
    },
    {
      field: 'enrolledAs', 
      headerName: 'Role', 
      width: 120, 
      sortable: false,
      renderCell: (params) => (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%', 
            width: '100%' 
          }}
        >
          <TextField 
            select 
            size="small" 
            variant="standard"
            value={params.value || 'trainee'}
            onChange={(ev) => updateEnrolledAs(params.row.employeeCode, ev.target.value)}
            sx={{ minWidth: 100 }}
          >
            {ENROLLED_AS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ),
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 140, 
      sortable: false,
      renderCell: (params) => {
        const isTrainee = (params.row.enrolledAs || 'trainee') === 'trainee';
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              height: '100%', 
              width: '100%',
            }}
          >
            <TextField 
              select 
              size="small" 
              variant="standard"
              disabled={!isTrainee}
              value={params.value || 'enrolled'}
              onChange={(ev) => updateStatus(params.row.employeeCode, ev.target.value)}
              sx={{ minWidth: 120 }}
            >
              {ATTENDANCE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        );
      },
    },
    ...scoreColumns,
    { 
      field: '_del', 
      headerName: '', 
      width: 60, 
      sortable: false, 
      filterable: false,
      renderCell: (params) => (
        <IconButton 
          color="error" 
          onClick={() => removeEnrollee(params.row.employeeCode)}
        >
          <Close />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      component="fieldset"
      sx={{
        gridColumn: '1 / -1',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1,
        m: 0,
        minWidth: 0,
      }}
    >
      <Box component="legend">
        <Typography variant="subtitle2">
          Attendance & Results ({enrollments.length}{maxCapacity ? ` / ${maxCapacity}` : ''})
        </Typography>
      </Box>

      <FlexBetween>
        <Box />
        {trainingCourseId && (
          <Tooltip title="Import attendees from linked course" arrow>
            <span>
              <Button
                size="small" variant="outlined"
                startIcon={isImporting ? <CircularProgress size={14} /> : <GroupAdd />}
                onClick={handleImportFromCourse}
                disabled={isImporting}
              >
                Import from Course
              </Button>
            </span>
          </Tooltip>
        )}
      </FlexBetween>

      <Box 
        display="flex" 
        alignItems="center" 
        gap={1} 
        my={1.5}
      >
        <Autocomplete
          size="small" 
          sx={{ flex: 1 }}
          options={employeeList.filter((c) => !enrollments.some((e) => e.employeeCode === c.employeeCode))}
          getOptionLabel={(o) => typeof o === 'string' ? o : `${o.employeeCode} - ${o.employeeName || ''}`}
          value={newEnrolleeCode}
          onChange={(_, v) => setNewEnrolleeCode(v)}
          isOptionEqualToValue={(o, v) => o.employeeCode === v?.employeeCode}
          renderInput={(params) => <TextField {...params} label="Add Participant" />}
          noOptionsText="No employees found"
        />
        <IconButton 
          size="small" 
          color="primary" 
          onClick={addEnrollee} 
          disabled={!newEnrolleeCode}
        >
          <Add />
        </IconButton>
      </Box>

      {enrollments.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No participants yet. Add individuals above{trainingCourseId && !isNew ? ' or import from the linked course.' : '.'}
        </Typography>
      ) : isMobile ? (
        <Stack spacing={1}>
          {enrollments.map((enr, i) => (
            <EnrollmentCard
              key={enr.employeeCode}
              enrollment={enr}
              index={i + 1}
              subjects={subjects || []}
              subjectsData={subjectsData}
              onRemove={removeEnrollee}
              onUpdateStatus={updateStatus}
              onUpdateEnrolledAs={updateEnrolledAs}
              onUpdateScore={updateScore}
              onUpdatePassed={updatePassed}
            />
          ))}
        </Stack>
      ) : (
        <DataGrid
          getRowId={(row) => row.employeeCode}
          rows={enrollments}
          columns={columns}
          autoHeight 
          hideFooter 
          disableRowSelectionOnClick
          sx={{ '& .MuiDataGrid-cell': { alignItems: 'center' } }}
        />
      )}
    </Box>
  );
};

export default SessionAttendance;
